"use client";

import { useState, useTransition } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import type { RestorationPost, RestorationPostStatus } from "@/lib/server-actions/restoration/_shared";
import { setPostAdminStatus, setPostProblemNumber } from "@/lib/server-actions/restoration/postAdmin";

interface PostAdminControlsProps {
  post: RestorationPost;
  /** 부분 필드 로컬 업데이트 (problem_number / post_status / is_confirmed) */
  onPostChanged: (
    postId: number,
    next: Partial<RestorationPost> | null
  ) => void;
  /** 정렬/그룹 재계산이 필요한 서버 리페치 트리거 */
  onRefreshNeeded: () => void;
}

/**
 * JCG-1572: 게시물별 관리자 전용 컨트롤.
 * - 문제 번호 맵핑 `<select>`: 미분류(null) 또는 1~20번 (setPostProblemNumber)
 * - 배지 상태 `<select>`: 확정 / 종합중 (관리자 게시물 한정, setPostAdminStatus)
 */
export default function PostAdminControls({
  post,
  onPostChanged,
  onRefreshNeeded,
}: PostAdminControlsProps) {
  const [isPending, startTransition] = useTransition();
  const [localProblemNumber, setLocalProblemNumber] = useState<number | null>(
    post.problem_number
  );
  const [localStatus, setLocalStatus] = useState<RestorationPostStatus | null>(
    post.post_status
  );

  const handleProblemNumberChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const raw = e.target.value;
    const next = raw === "" ? null : Number(raw);
    if (next === localProblemNumber) return;
    setLocalProblemNumber(next);
    startTransition(async () => {
      const result = await setPostProblemNumber(post.id, next);
      if (result.success) {
        toast.success(
          next === null ? "미분류로 이동했습니다." : `${next}번으로 맵핑했습니다.`
        );
        onPostChanged(post.id, { problem_number: next });
        onRefreshNeeded();
      } else {
        toast.error(result.error || "맵핑 변경에 실패했습니다.");
        setLocalProblemNumber(post.problem_number);
      }
    });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value as RestorationPostStatus;
    if (next === localStatus) return;
    const prev = localStatus;
    setLocalStatus(next);
    startTransition(async () => {
      const result = await setPostAdminStatus(post.id, next);
      if (result.success) {
        toast.success(
          next === "confirmed" ? "확정으로 변경했습니다." : "종합중으로 변경했습니다."
        );
        onPostChanged(post.id, {
          post_status: next,
          is_confirmed: next === "confirmed",
        });
        onRefreshNeeded();
      } else {
        toast.error(result.error || "배지 변경에 실패했습니다.");
        setLocalStatus(prev);
      }
    });
  };

  return (
    <div className="mt-3 rounded-md border border-amber-200 dark:border-amber-900/60 bg-amber-50/40 dark:bg-amber-950/20 px-3 py-2 flex flex-wrap items-center gap-3">
      <span className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wider">
        관리자
      </span>

      <label className="flex items-center gap-1 text-xs text-gray-700 dark:text-neutral-300">
        문제 번호
        <select
          value={localProblemNumber ?? ""}
          onChange={handleProblemNumberChange}
          disabled={isPending}
          className="h-7 rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-2 text-xs disabled:opacity-60"
        >
          <option value="">미분류</option>
          {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n}번
            </option>
          ))}
        </select>
      </label>

      {localStatus !== null && (
        <label className="flex items-center gap-1 text-xs text-gray-700 dark:text-neutral-300">
          배지
          <select
            value={localStatus}
            onChange={handleStatusChange}
            disabled={isPending}
            className="h-7 rounded-md border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-2 text-xs disabled:opacity-60"
          >
            <option value="confirmed">확정</option>
            <option value="editing">종합중</option>
          </select>
        </label>
      )}

      {isPending && (
        <Loader2 className="w-3 h-3 animate-spin text-amber-700 dark:text-amber-300" />
      )}
    </div>
  );
}
